// Third Party
import React, { ReactElement, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useParams } from 'react-router-dom';

// Components
import { Button, Loader, Switch, Typography } from 'components';
import { ButtonBar, TutorialButton, WidgetTable } from 'components/machine-health';

// Providers
import { useWidgetTableContext } from 'components/machine-health/configurator/WidgetTable/WidgetTableContext';
import { useLanguage } from 'providers';

// API
import {
  useCreateMachineConfigurationDataMutation,
  useGetMachineConfiguratorDataQuery,
  useUpdateMachineConfiguratorDataMutation
} from 'api';

// Types
import { MachineHealthSubTabs, MachineHealthTabs, WidgetClass, WidgetType } from 'types/protein';
import { WidgetTableDataItem } from 'types/machine-health';
import { v4 as uuidv4 } from 'uuid';

// Constants
import { tutorialPlaceholderStepsData, WidgetTableColumnConfigs } from 'constants/machineConfig';

// Helpers
import { ToastMsg } from 'common/components/Toast';

// Hooks
import { useCopyConfiguration } from '../../hooks';

// Common
import CopyConfigurationActions from '../../components/CopyConfigurationActions';

// Styling
const Container = styled.div<{ removePadding?: boolean }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: ${({ removePadding }) => (removePadding ? '0' : '0 6rem')};

  width: 100%;
`;

const WidgetTableContainer = styled.div`
  display: flex;
  gap: 1.375rem;
  flex-direction: column;
  flex-grow: 1;
  padding-bottom: 1.375rem;
  width: 100%;
`;

interface Props {
  copyWidgetType?: WidgetType;
  handleCopyCallback?: (item: WidgetTableDataItem) => void;
  hasWidgets?: boolean;
  hideButtonRow?: boolean;
  isCopyTab?: boolean;
  isDirty: boolean;
  overrideMachineId?: string;
  removePadding?: boolean;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
}

const MachineProductProcessing = ({
  copyWidgetType,
  handleCopyCallback,
  hasWidgets,
  hideButtonRow,
  isCopyTab,
  isDirty,
  overrideMachineId,
  removePadding,
  setIsDirty
}: Props): ReactElement => {
  const theme = useTheme();
  const { machineId } = useParams<{ machineId: string }>();
  const { languageId } = useLanguage();
  const {
    data,
    isFetching: fetching,
    error
  } = useGetMachineConfiguratorDataQuery({
    machineId: overrideMachineId || machineId,
    labels: [MachineHealthTabs.ProductProcessing],
    languageId: languageId,
    showInactive: true
  });

  const [updateProductProcessingData, { isLoading: updating, error: errorUpdate }] =
    useUpdateMachineConfiguratorDataMutation();
  const [createMachineConfigurationData] = useCreateMachineConfigurationDataMutation();

  const overviewConfig = data?.[0]?.members?.find(
    (member) => member.label === MachineHealthSubTabs.PPOverview
  );

  const isDefrostPredictionConfigured =
    overviewConfig?.members?.some(
      (member) =>
        member.label === MachineHealthSubTabs.PPOverviewDefrostPrediction
    ) ?? false;

  const { localTableActive, localTableNames, localTableRows } = useWidgetTableContext();
  const [tabData, setTabData] = useState<WidgetTableDataItem | undefined>(data?.[0]);
  const [isActive, setIsActive] = useState(!!data?.[0]?.active);

  const {
    copiedConfiguration,
    copyModalOpen,
    copyFetching,
    updatingConfiguration,
    errorCopy,
    errorUpdateConfiguration,
    setCopyModalOpen,
    copyConfiguration,
    onCopyFromSelectedMachine,
    openCopyModal
  } = useCopyConfiguration({
    tabData,
    setTabData,
    setIsDirty,
    machineHealthTabLabel: MachineHealthTabs.ProductProcessing
  });

  const saveInfo = async () => {
    // Do nothing if we have no tab data
    if (!tabData) {
      return;
    }
    // Reconstruct data to send to server
    const updatedProductProcessingTab = {
      ...tabData,
      active: isActive,
      members: localTableRows
        .filter((row) => tabData.members?.some((tab) => tab.id === row.id))
        .map((row) => ({
          ...row,
          // Update the name and active status of each member(tab) with local state
          name: localTableNames.find((name) => name.id === row.id)?.name,
          active: localTableActive.find((active) => active.id === row.id)?.active
        }))
    };

    await updateProductProcessingData({
      machineId,
      widget: updatedProductProcessingTab,
      languageId: languageId
    })
      .unwrap()
      .then(() => {
        ToastMsg({
          heading: 'Success',
          type: 'success',
          message: `Product processing updated successfully`
        });
      })
      .catch((error) => {
        ToastMsg({
          heading: 'Error',
          type: 'error',
          message: `Failed to update product processing`
        });
        console.error(error?.data?.detail);
      });
    setIsDirty(false);
  };

  useEffect(() => {
    setTabData(data?.[0]);
    setIsActive(!!data?.[0]?.active);
  }, [data]);

  useEffect(() => {
    if (!overviewConfig || isDefrostPredictionConfigured) {
      return;
    }

    void createMachineConfigurationData({
      machineId: overrideMachineId || machineId,
      configuration: {
        id: uuidv4(),
        name: 'Defrost Prediction',
        names: {
          en: 'Defrost Prediction'
        },
        label: MachineHealthSubTabs.PPOverviewDefrostPrediction,
        widgetClass: WidgetClass.Widget,
        widgetType: WidgetType.KpiCard,
        editable: true,
        deletable: true,
        active: false,
        toggleActive: true,
        reorder: true,
        parent_id: overviewConfig.id,
        tags: []
      },
      languageId
    })
      .unwrap()
      .catch((error) => {
        console.error(error?.data?.detail);
      });
  }, [
    createMachineConfigurationData,
    isDefrostPredictionConfigured,
    languageId,
    machineId,
    overviewConfig?.id,
    overrideMachineId
  ]);

  useEffect(() => {
    setIsDirty(false);
  }, []);

  const handleEnableClick = () => {
    setIsActive((prev) => !prev);
    setIsDirty(true);
  };

  const isFetching = fetching || updating || copyFetching || updatingConfiguration;
  const isError = error || errorUpdate || errorCopy || errorUpdateConfiguration;

  if (isFetching) {
    return <Loader />;
  }

  if (isError) {
    console.error(isError);
    <Typography color="negativeRed">Failed to load data</Typography>;
  }

  return (
    <Container removePadding={removePadding}>
      {!hideButtonRow && (
        <ButtonBar columnGap="1.5rem" title="" padding="1.375rem 0">
          {tabData && !copiedConfiguration && (
            <>
              <Switch
                checked={isActive}
                handleDiameter={22}
                height={14}
                onChange={handleEnableClick}
                width={40}
                offColor={theme.colors.mediumGrey2}
                offHandleColor={theme.colors.mediumGrey3}
              />

              <Button
                disabled={!isDirty}
                onClick={() => saveInfo()}
                variant={'primary'}
                width="fit-content"
              >
                Save
              </Button>
            </>
          )}

          <CopyConfigurationActions
            copiedConfiguration={copiedConfiguration}
            copyConfiguration={copyConfiguration}
            copyModalOpen={copyModalOpen}
            hasWidgets={hasWidgets}
            onCopyFromSelectedMachine={onCopyFromSelectedMachine}
            openCopyModal={openCopyModal}
            setCopyModalOpen={setCopyModalOpen}
          />

          <TutorialButton stepsData={tutorialPlaceholderStepsData} />
        </ButtonBar>
      )}
      <WidgetTableContainer>
        {tabData?.members?.length ? (
          tabData.members.map((widgetTable) => (
            <WidgetTable
              columnConfigs={WidgetTableColumnConfigs}
              copyTable={{
                copyWidgetType,
                handleCopyCallback,
                isCopyTable: isCopyTab
              }}
              data={widgetTable.members}
              isDataLoading={isFetching}
              key={widgetTable.id}
              parent={widgetTable}
              setIsDirty={setIsDirty}
              shouldRenderTags={false}
            />
          ))
        ) : (
          <Typography color="greyfont">No data found</Typography>
        )}
      </WidgetTableContainer>
    </Container>
  );
};

export default MachineProductProcessing;
